import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';
import { getCurrentTimesheet, ApiClientError } from '../api/client';
import { getPeriods } from '../api/periods';
import { getTimesheetSettings } from '../api/timesheetSettings';
import type { TimesheetSettings } from '../api/timesheetSettings';
import { getDraft } from '../api/draft';
import { freshnessText } from '../utils/freshness';
import { reconcileDraft, applyVpWbsRequirements, rowsNeedingLeaf, leafNeededMessage } from '../utils/reconcileDraft';
import { useVpWbsRequirements } from './useVpWbsRequirements';
import { formatDateRange } from '../utils/period';
import { useTriageStore } from '../stores/triage';
import { useTimezoneStore } from '../stores/timezone';
import type { TimesheetRow, TimesheetResponse } from '../api/client';
import type { PeriodsResponse, PeriodItem } from '../api/periods';
import type { DraftResponse, DraftTrigger } from '../api/draft';


export function useDraftQueries() {
  const router = useRouter();
  const triageStore = useTriageStore();
  const tzStore = useTimezoneStore();
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Active period — sourced from the triage store (set by the periods watcher
  // below, persisted across reloads). Both the VP and draft queries key off it.
  // ---------------------------------------------------------------------------
  const activePeriodStart = computed((): string => triageStore.activePeriodStart ?? '');
  const activePeriodEnd = computed((): string => triageStore.activePeriodEnd ?? '');

  // ---------------------------------------------------------------------------
  // Timesheet query (VP rows for the grid) — keyed by the selected period so a
  // period switch refetches that period's VP rows (Bug 5954).
  // ---------------------------------------------------------------------------
  const tsQuery = useQuery<TimesheetResponse, ApiClientError>({
    queryKey: computed(() => ['timesheet', activePeriodEnd.value]),
    queryFn: () => getCurrentTimesheet(activePeriodEnd.value),
    enabled: computed(() => !!activePeriodEnd.value),
    retry: false,
    staleTime: 30_000,
  });

  const tsError = computed(() => tsQuery.error.value);
  const vpRows = computed((): TimesheetRow[] => tsQuery.data.value?.rows ?? []);

  watch(() => tsQuery.error.value, (err) => {
    if (err instanceof ApiClientError && err.status === 401) {
      void router.replace({ path: '/login', query: { flash: 'Session expired, please sign in again.' } });
    }
  });

  // ---------------------------------------------------------------------------
  // Periods query
  // ---------------------------------------------------------------------------
  const periodsQuery = useQuery<PeriodsResponse, ApiClientError>({
    queryKey: ['timesheet', 'periods'],
    queryFn: getPeriods,
    staleTime: Infinity,
    retry: false,
  });

  const periodsLoading = computed(() => periodsQuery.isLoading.value);
  const periods = computed((): PeriodItem[] => periodsQuery.data.value?.periods ?? []);

  // Tenant note requirement — drives the picker's required-note step. VP encodes
  // RequireComments as Y / N / C (and " " unset); anything not explicitly "N"
  // requires a note (matches Verify's gate; see Bug 5956 follow-up).
  const settingsQuery = useQuery<TimesheetSettings, ApiClientError>({
    queryKey: ['timesheet-settings'],
    queryFn: getTimesheetSettings,
    retry: false,
    staleTime: 300_000,
  });
  const commentRequired = computed(() => {
    const v = settingsQuery.data.value?.requireComments?.trim();
    return !!v && v !== 'N';
  });

  watch(() => periodsQuery.data.value, (data) => {
    if (!data || triageStore.activePeriodEnd) return;
    const current = data.periods.find(p => p.isCurrent) ?? data.periods[0];
    if (current) {
      triageStore.activePeriodStart = current.startDate;
      triageStore.activePeriodEnd = current.endDate;
    }
  }, { immediate: true });

  // ---------------------------------------------------------------------------
  // Draft query
  // ---------------------------------------------------------------------------
  // Explicit rebuild trigger for the next fetch. Reactive refetches (period / tz
  // change) use 'auto' — which now loads the saved draft when present (ADR-0031);
  // the Regenerate actions set this, then refetch to force a rebuild.
  const nextTrigger = ref<DraftTrigger>('auto');
  // True while a user-initiated regenerate / update-draft refetch is in
  // flight. vue-query's isLoading is false on a refetch (data already present),
  // so the initial-load spinner doesn't cover these — this drives a busy scrim.
  const regenerating = ref(false);

  const draftQuery = useQuery<DraftResponse, ApiClientError>({
    queryKey: computed(() => ['draft', activePeriodStart.value, activePeriodEnd.value, tzStore.selectedZone]),
    queryFn: () => getDraft(activePeriodStart.value || undefined, activePeriodEnd.value || undefined, tzStore.selectedZone || undefined, nextTrigger.value),
    enabled: computed(() => !!activePeriodStart.value),
    retry: false,
    staleTime: 60_000,
  });

  const draftLoading = computed(() => draftQuery.isLoading.value);
  // Refetch-aware, unlike isLoading: after a save the caches are invalidated
  // but still hold the pre-save data, so the screen renders stale rows and then
  // swaps under the user. This is what the post-save scrim waits on (#6487).
  const refreshing = computed(() => draftQuery.isFetching.value || tsQuery.isFetching.value);
  const draftError = computed(() => draftQuery.error.value);
  const draft = computed((): DraftResponse | null => draftQuery.data.value ?? null);

  // The engine draft with the user's edit overlay applied — what the panel
  // renders. Picked needs-input events leave the queue and land in the grid;
  // re-targeted cells move. Recomputes whenever the overlay changes.
  const reconciledDraft = computed((): DraftResponse | null =>
    draft.value ? reconcileDraft(draft.value, [...triageStore.drafts.values()]) : null,
  );
  const vpRequirements = useVpWbsRequirements(reconciledDraft);
  const effectiveDraft = computed((): DraftResponse | null =>
    reconciledDraft.value ? applyVpWbsRequirements(reconciledDraft.value, vpRequirements.value) : null,
  );

  // ---------------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------------
  const draftedHours = computed((): number => {
    if (!effectiveDraft.value) return 0;
    return effectiveDraft.value.rows.reduce((sum, row) =>
      sum + row.days.reduce((s, d) => s + d.hours, 0), 0,
    );
  });

  const targetHours = 80;

  // The new-events check: the draft above reads no source, so drift comes from
  // this second request once it has rendered (ADR-0031 amendment 2026-09-26).
  // Its own key, outside ['draft'], so a save's refetch of the draft neither
  // waits on it nor reruns it; keyed on generatedAt so a regenerate reruns it.
  const checkQuery = useQuery<DraftResponse, ApiClientError>({
    queryKey: computed(() => ['draft-check', activePeriodStart.value, activePeriodEnd.value, tzStore.selectedZone, draft.value?.generatedAt]),
    queryFn: () => getDraft(activePeriodStart.value || undefined, activePeriodEnd.value || undefined, tzStore.selectedZone || undefined, 'check'),
    enabled: computed(() => !!draft.value?.generatedAt),
    retry: false,
    staleTime: 60_000,
  });
  const checkingForNewEvents = computed(() => checkQuery.isFetching.value);
  // A failed check has no drift to report, which must not read as "no new events".
  const newEventsCheckFailed = computed(() => checkQuery.isError.value);

  // Only New is surfaced. A changed or removed event's drafted row stays until
  // the user updates the draft or removes the row; the UI does not flag it
  // (accepted trade-off, ADR-0031 amendment 2026-09-28).
  const newEventsCount = computed((): number => checkQuery.data.value?.drift?.new ?? 0);

  // Saved-draft persistence status (ADR-0031). 'error' still means the edit is
  // held in sessionStorage, so the honest line is "saved on device".
  const saveStatusText = computed((): string => {
    switch (triageStore.saveStatus) {
      case 'saving': return 'Saving…';
      case 'saved':  return 'Saved';
      case 'error':  return 'Saved on device';
      default:       return '';
    }
  });

  const progressPct = computed(() =>
    targetHours > 0 ? Math.min((draftedHours.value / targetHours) * 100, 100) : 0,
  );

  const progressBarStyle = computed(() => ({
    '--tc-triage-progress-pct': `${progressPct.value}%`,
  }));

  // Draft rows still missing a level VP requires: a phase, or a task under it.
  const leafNeeded = computed(() => rowsNeedingLeaf(effectiveDraft.value));
  const phasedNeedingLeafCount = computed((): number => leafNeeded.value.phase + leafNeeded.value.task);
  const leafNeededText = computed(() => leafNeededMessage(leafNeeded.value));

  const canVerify = computed(() =>
    (effectiveDraft.value?.rows.length ?? 0) > 0 && phasedNeedingLeafCount.value === 0,
  );

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Freshness footer — recomputes every 60s while tab is visible
  // ---------------------------------------------------------------------------
  const now = ref(new Date());
  let freshnessInterval: ReturnType<typeof setInterval> | null = null;

  function tickFreshness(): void {
    if (!document.hidden) {
      now.value = new Date();
    }
  }

  function onVisibilityChange(): void {
    if (!document.hidden) {
      now.value = new Date();
    }
  }

  onMounted(() => {
    freshnessInterval = setInterval(tickFreshness, 60_000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', tickFreshness);
  });

  onUnmounted(() => {
    if (freshnessInterval !== null) clearInterval(freshnessInterval);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', tickFreshness);
  });

  const freshnessLine = computed((): string =>
    freshnessText(draft.value?.generatedAt ?? undefined, draft.value?.trigger, now.value),
  );

  // ---------------------------------------------------------------------------
  // Period helpers
  // ---------------------------------------------------------------------------
  // VP reports a period's status as Closed for genuinely-closed *current/past*
  // periods, but also (spuriously) for *future* periods — so only a non-future
  // period's Closed status is trustworthy. The timesheet read's `editable` flag is
  // unreliable here (it reads "Y" for a closed current period); the period-config
  // status is the better signal. This is a best-effort pre-warning; the save-time
  // rejection (stale_master) is the reliable backstop.
  function isPeriodClosed(p: PeriodItem): boolean {
    const todayIso = new Date().toISOString().slice(0, 10);
    return p.startDate <= todayIso && p.status.trim().toLowerCase() === 'closed';
  }

  const activePeriodClosed = computed((): boolean => {
    const p = periods.value.find((x) => x.endDate === activePeriodEnd.value);
    return !!p && isPeriodClosed(p);
  });

  function formatPeriodLabel(p: PeriodItem): string {
    const year = p.endDate.split('-')[0];
    const label = `${formatDateRange(p.startDate, p.endDate)}, ${year}`;
    const suffix = p.isCurrent ? ' (current)' : '';
    const state = periodStateWord(p);
    return `${label}${suffix}${state ? ` · ${state}` : ''}`;
  }

  // VP's word for the period in hand, from the timesheet read — the same field
  // the write gate gates on, so what the grid says and what a publish will do
  // cannot disagree. This is the sheet's own state, so every state is worth
  // saying, including the unremarkable one.
  const vpStateLabel = computed((): string | null => {
    switch (tsQuery.data.value?.state) {
      case 'none': return 'No timesheet';
      case 'in-progress': return 'In progress';
      case 'submitted': return 'Submitted';
      case 'posted': return 'Posted';
      case 'closed': return 'Closed';
      default: return null;
    }
  });

  // The picker's word for a period, from the config — one call covers all 24,
  // where a read covers one. Every option is answered from the same source, so a
  // period's label does not change depending on which period is open.
  function configStateWord(status: string): string | null {
    switch (status.trim().toLowerCase()) {
      case 'submitted': return 'Submitted';
      case 'posted': return 'Posted';
      case 'closed': return 'Closed';
      default: return null;
    }
  }

  // VP reports a period's state as Closed for genuinely-closed current/past
  // periods and, spuriously, for future ones — so a future period is left unsaid.
  function periodStateWord(p: PeriodItem): string | null {
    const todayIso = new Date().toISOString().slice(0, 10);
    if (p.startDate > todayIso) return null;
    return configStateWord(p.status);
  }

  const periodLabel = computed((): string => {
    if (!activePeriodStart.value || !activePeriodEnd.value) return '';
    return formatDateRange(activePeriodStart.value, activePeriodEnd.value);
  });

  // ---------------------------------------------------------------------------
  // Period change
  // ---------------------------------------------------------------------------
  function onPeriodChange(endDate: string): void {
    const period = periods.value.find(p => p.endDate === endDate);
    if (!period) return;
    triageStore.activePeriodStart = period.startDate;
    triageStore.activePeriodEnd = period.endDate;
    void queryClient.invalidateQueries({ queryKey: ['draft'], exact: false });
  }

  // ---------------------------------------------------------------------------
  // Regenerate actions
  // ---------------------------------------------------------------------------
  async function onUpdateDraft(): Promise<void> {
    nextTrigger.value = 'regenerate_unresolved';
    regenerating.value = true;
    try {
      await draftQuery.refetch();
    } finally {
      nextTrigger.value = 'auto';
      regenerating.value = false;
    }
  }

  async function doReDraft(): Promise<void> {
    triageStore.clearDrafts();
    nextTrigger.value = 'redraft_all';
    regenerating.value = true;
    try {
      await draftQuery.refetch();
    } finally {
      nextTrigger.value = 'auto';
      regenerating.value = false;
    }
  }

  return {
    triageStore,
    activePeriodStart,
    activePeriodEnd,
    tsError,
    vpRows,
    periodsLoading,
    periods,
    commentRequired,
    regenerating,
    draftLoading,
    refreshing,
    draftError,
    draft,
    effectiveDraft,
    draftedHours,
    targetHours,
    checkingForNewEvents,
    newEventsCheckFailed,
    newEventsCount,
    saveStatusText,
    progressBarStyle,
    phasedNeedingLeafCount,
    leafNeededText,
    canVerify,
    // Quick-triage filter (C3)
    freshnessLine,
    vpStateLabel,
    activePeriodClosed,
    formatPeriodLabel,
    periodLabel,
    onPeriodChange,
    onUpdateDraft,
    doReDraft,
  };
}
